import { Controller } from "@hotwired/stimulus";
import * as HelloSign from "hellosign-embedded";

export default class extends Controller {
  static values = {
    testMode: Boolean,
  };

  connect() {
    this._pollInterval = null;
    this._signedSignatureId = null;
  }

  disconnect() {
    this._stopPolling();
  }

  fetchContractForSubscribe(event) {
    const target = event.currentTarget;
    const signatureID = target.dataset.signatureid;
    fetch(`/legal_signatures.json?signature_id=${signatureID}`)
      .then((response) => response.json())
      .then((data) => {
        if (data.already_signed) {
          this._onAlreadySigned(signatureID);
          return;
        }
        this.showContractModal(data);
      });
  }

  _onAlreadySigned(signatureId) {
    this._disableSignatureLink(signatureId);
    this._updateSignatureCard(
      signatureId,
      "Signed",
      "bg-success",
      "border-color:green"
    );
    this._reloadPage();
  }

  showContractModal(data) {
    const client = new HelloSign();

    client.on("sign", (payload) => {
      this._onSign(payload.signatureId);
    });

    client.on("decline", (payload) => {
      this._updateSignatureCard(
        payload.signatureId,
        "Declined",
        "bg-danger",
        "border-color:red"
      );
    });

    client.on("error", (payload) => {
      this._updateSignatureCard(
        payload.signatureId,
        "Error",
        "bg-danger",
        "border-color:red"
      );
    });

    client.open(data.sign_url, {
      testMode: this.testModeValue,
      clientId: data.client_id,
      skipDomainVerification: this.testModeValue,
    });
  }

  _onSign(signatureId) {
    this._signedSignatureId = signatureId;
    this._disableSignatureLink(signatureId);
    this._updateSignatureCard(
      signatureId,
      "Confirming...",
      "bg-warning",
      "border-color:orange"
    );
    if (this._isLastSignature(signatureId)) {
      this._showProcessingBanner();
    }
    this._startPolling(signatureId);
  }

  _showProcessingBanner() {
    if (this.element.querySelector("[data-processing-banner]")) return;

    const banner = document.createElement("div");
    banner.setAttribute("data-processing-banner", "");
    banner.className = "bg-light rounded p-4 mt-4 text-center";
    banner.innerHTML =
      '<i class="fa-duotone fa-spinner-third fa-spin me-2"></i>' +
      "Processing your signature, please wait...";
    this.element.appendChild(banner);
  }

  _updateSignatureCard(signatureId, label, bgClass, borderStyle) {
    const card = this.element.querySelector(
      `[data-signature-id="${signatureId}"]`
    );
    if (!card) return;

    card.style = borderStyle;
    const badge = card.querySelector("[class*='bg-']");
    if (badge) {
      badge.className = badge.className.replace(/bg-\w+/, bgClass);
      badge.lastElementChild && (badge.lastElementChild.textContent = label);
    }
  }

  _disableSignatureLink(signatureId) {
    const card = this.element.querySelector(
      `[data-signature-id="${signatureId}"]`
    );
    const link = card && card.closest("a");
    if (link) link.removeAttribute("data-action");
  }

  _reloadPage() {
    window.Turbo.visit(window.location, { action: "replace" });
  }

  _isLastSignature(signatureId) {
    const cards = this.element.querySelectorAll("[data-signature-id]");
    const unsignedCards = Array.from(cards).filter((card) => {
      if (card.dataset.signatureId === signatureId) return false;
      return card.style.borderColor !== "green";
    });
    return unsignedCards.length === 0;
  }

  _startPolling(signatureId) {
    this._stopPolling();
    let attempts = 0;
    const maxAttempts = 12;

    this._pollInterval = setInterval(() => {
      attempts++;
      if (attempts > maxAttempts) {
        this._stopPolling();
        return;
      }

      fetch(`/legal_signatures/status.json?signature_id=${signatureId}`)
        .then((response) => response.json())
        .then((data) => {
          if (
            data.status_code === "signed" ||
            data.is_complete ||
            data.is_declined ||
            data.has_error
          ) {
            this._stopPolling();
            this._reloadPage();
          }
        })
        .catch(() => {});
    }, 5000);
  }

  _stopPolling() {
    if (this._pollInterval) {
      clearInterval(this._pollInterval);
      this._pollInterval = null;
    }
  }
}
